import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { TaskTimeline } from "@/components/TaskTimeline";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

/**
 * Timeline view — tasks as bars on a horizontal date axis (start→due), with
 * zoom, group-by, milestone markers and dependency connectors. Sits beside the
 * board/list/table/calendar leaf routes under the project shell.
 */
function TimelineView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();

  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const msQ = useQuery({
    queryKey: queryKeys.milestonesFor(projectId),
    queryFn: () => api.listMilestones(projectId),
  });
  const edgesQ = useQuery({
    queryKey: queryKeys.projectDeps(projectId),
    queryFn: () => api.projectDependencies(projectId),
  });
  const epicsQ = useQuery({
    queryKey: queryKeys.epicsFor(projectId),
    queryFn: () => api.listEpics(projectId),
  });

  const users = useMemo(() => {
    const map = new Map<string, { name: string; image: string | null }>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, {
        name: m.name ?? m.email ?? m.id.slice(0, 8),
        image: m.image ?? null,
      });
    }
    return map;
  }, [teamQ.data]);

  return (
    <div className="proj-shell-content">
      <div className="board-bar" style={{ marginBottom: 12 }}>
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
            { value: "gantt", label: "Gantt", icon: "chev" },
          ]}
          value="timeline"
          onChange={(v) =>
            navigate({
              to:
                v === "list"
                  ? "/projects/$projectId/list"
                  : v === "table"
                    ? "/projects/$projectId/table"
                    : v === "calendar"
                      ? "/projects/$projectId/calendar"
                      : v === "gantt"
                        ? "/projects/$projectId/gantt"
                        : v === "timeline"
                          ? "/projects/$projectId/timeline"
                          : "/projects/$projectId",
              params: { projectId },
            })
          }
        />
      </div>

      {tasksQ.isLoading ? (
        <Skeleton count={6} height={40} />
      ) : tasksQ.isError ? (
        <p className="t-muted" style={{ padding: 24 }}>
          Couldn&rsquo;t load tasks for the timeline. Try again.
        </p>
      ) : (tasksQ.data?.tasks ?? []).length === 0 ? (
        <EmptyState
          icon="arrow"
          title="No tasks to plot yet"
          caption="The timeline lays out tasks by their start and due dates. Add dated tasks to see them here."
        />
      ) : (
        <TaskTimeline
          projectId={projectId}
          tasks={tasksQ.data?.tasks ?? []}
          users={users}
          milestones={msQ.data?.milestones ?? []}
          epicNames={
            new Map((epicsQ.data?.epics ?? []).map((e) => [e.id, e.name]))
          }
          edges={edgesQ.data?.edges ?? []}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/timeline")({
  component: TimelineView,
});
